// Observation is not rendering: only an authoritative `failed` job response
// can end a render. Sleep, offline time and HTTP errors retain its identity.
import type { TextEditDiagnostic } from "@shared/text-edit-diagnostics";
interface EditStatus {
  status: "pending" | "ready" | "failed";
  image_url?: string;
  message?: string;
  phase?: string;
  diagnostic?: TextEditDiagnostic | null;
}
interface StatusResponse {
  ok: true;
  status: number;
  json: () => Promise<EditStatus>;
}
const requests = new Map<string, Promise<StatusResponse>>();
const delays = new Map<string, number>();

export function readImageEditStatus(jobId: string): Promise<StatusResponse> {
  const existing = requests.get(jobId);
  if (existing) return existing;
  const request = (async () => {
    if (navigator.onLine === false || document.visibilityState === "hidden") {
      throw new Error("Observation paused");
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    try {
      const status = await Promise.race([
        (async () => {
          const response = await fetch(`/api/edit-image/${jobId}`, {
            credentials: "include", signal: controller.signal,
          });
          // Authorization/not-found errors are NOT evidence of render failure.
          if (!response.ok) throw new Error(`Status unavailable (${response.status})`);
          const body = await response.json();
          if (!["pending", "ready", "failed"].includes(body?.status)) throw new Error("Invalid edit status");
          return body;
        })(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error("Status check interrupted"));
          }, 20_000);
        }),
      ]);
      delays.delete(jobId);
      return { ok: true as const, status: 200, json: async () => status };
    } finally {
      clearTimeout(timer!);
    }
  })();
  requests.set(jobId, request);
  void request.catch(() => {
    delays.set(jobId, Math.min(30_000, (delays.get(jobId) ?? 1500) * 2));
  }).finally(() => { requests.delete(jobId); });
  return request;
}

// A single sleeping loop owns each poller. Recovery events wake that loop,
// never start another one, submit a render or authorize saving a preview.
export function waitForImageEditCheck(jobId: string): Promise<void> {
  return new Promise(resolve => {
    const manual = (event: Event) => {
      if ((event as CustomEvent).detail === jobId) wake();
    };
    const wake = () => {
      if (document.visibilityState === "hidden" || navigator.onLine === false) return;
      finish();
    };
    const finish = () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("image-edit-check", manual);
      resolve();
    };
    const timer = setTimeout(finish, delays.get(jobId) ?? 3000);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    window.addEventListener("image-edit-check", manual);
  });
}

export function checkImageEditNow(jobId: string) {
  window.dispatchEvent(new CustomEvent("image-edit-check", { detail: jobId }));
}
